/**
 * The launcher — an OS-style grid of the tenant's curated apps.
 *
 * A platform admin curates one ordered set per tenant; the server returns the
 * subset THIS caller holds read on, already in the admin's order (plan §2). So
 * the view sorts nothing and filters nothing: re-deriving either here would be
 * a second, weaker implementation of a decision the server already made.
 *
 * Launching is `onOpen(fileId)` → `${prefix}/app/<fileId>`, the same path the
 * dock and "Open as app" use. This grid is a VISIBILITY surface, never an
 * access one — anyone can paste that URL, and the launch is gated
 * independently server-side (features/files/api/__tests__/launcher-launch-gate.test.ts).
 */
import { usePageTitle } from '@papan/brand/react'
import { useTranslation } from '@papan/i18n'
import { MiniappIcon, greetingForHour } from '@papan/core-ui/ui'
import { parseMiniappIcon, parseMiniappIconRev } from '@papan/types'
import { useLauncherShortcuts, type LauncherShortcut } from '../client/queries.js'

export interface LauncherViewProps {
  tenantId: string
  /** Active org identity — part of the cache key; two seats in one org can
   *  legitimately see different grids. */
  orgEmail?: string | null
  /** Hold the fetch until the shell knows which tenant it is acting as. */
  enabled?: boolean
  onOpen: (fileId: string) => void
  /** Signed-in user's display name — greeted by first name when present. */
  userName?: string
  /** Organization name, shown above the greeting. Omit on personal tenants. */
  orgName?: string | null
  /** Is this deck the one on screen? The shell keeps hidden windows MOUNTED,
   *  so without it every backgrounded surface would go on naming the tab. */
  deckActive?: boolean
}

/** Rendered edge length of a tile icon, in CSS px. */
const TILE_ICON_PX = 64

function Tile({ shortcut, onOpen }: { shortcut: LauncherShortcut; onOpen: (id: string) => void }) {
  // The admin's caption wins when set; otherwise the app's own name. `label`
  // is what a client calls the app internally ("Claims"), which is often not
  // what its author named the folder.
  const caption = shortcut.label?.trim() || shortcut.name
  return (
    <button
      onClick={() => onOpen(shortcut.fileId)}
      className="flex flex-col items-center gap-2 min-w-0 p-2 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
      title={caption}
      data-testid="launcher-tile"
      data-file-id={shortcut.fileId}
    >
      <MiniappIcon
        spec={parseMiniappIcon(shortcut.icon)}
        name={caption}
        size={TILE_ICON_PX}
        // A mount's composited PNG is unreachable from this tenant (see the
        // DTO note), so don't ask for it — the spec placeholder is what would
        // render anyway, and this way it renders without a 404 first.
        refId={shortcut.mounted ? null : shortcut.fileId}
        rev={parseMiniappIconRev(shortcut.icon)}
        className="chat-app-tile shrink-0"
      />
      <span className="w-full truncate text-center text-xs themed-text-secondary">{caption}</span>
    </button>
  )
}

export function LauncherView({ tenantId, orgEmail, enabled = true, onOpen, userName, orgName, deckActive = true }: LauncherViewProps) {
  const { t } = useTranslation('core-ui')
  // The taskbar's own label for this surface — one string, so the tab and the
  // rail can never disagree, and it is translated like the rest of the SPA.
  usePageTitle(t('taskBar.launcher'), deckActive)
  const { data, isLoading, isError } = useLauncherShortcuts({ tenantId, orgEmail, enabled })
  // Read at render, like the chat home: a landing surface is mounted fresh on
  // arrival, and a greeting that re-computed on a timer would rewrite itself
  // under someone reading it.
  const greeting = greetingForHour(new Date().getHours())
  const firstName = userName?.trim().split(/\s+/)[0]

  const body = () => {
    // `isLoading` is first-load only (a background refetch keeps showing the
    // current grid), so this never blanks a populated launcher on a realtime
    // invalidation.
    if (isLoading) return <p className="text-sm themed-text-muted text-center">{t('launcher.loading')}</p>
    if (isError) return <p className="text-sm themed-text-muted text-center">{t('launcher.error')}</p>
    if (!data || data.length === 0) {
      // Deliberately does NOT distinguish "nothing curated" from "curated,
      // none readable by you". The second phrasing would confirm to a member
      // that apps they cannot open exist — the grid's whole job is to not do
      // that. An operator diagnoses the difference on the admin screen.
      return (
        <div className="max-w-md mx-auto text-center">
          <p className="text-sm themed-text-secondary">{t('launcher.empty.title')}</p>
          <p className="mt-1 text-sm themed-text-muted">{t('launcher.empty.hint')}</p>
        </div>
      )
    }
    return (
      // `auto-FIT` with fixed 104px tracks, then `justify-center`. Two choices
      // here and both were measured, because the wrong one still LOOKS right
      // on a full row:
      //   - `minmax(104px,1fr)` lets tracks absorb the leftover width, so three
      //     apps stretch across the whole row under a centred greeting;
      //   - `auto-FILL` keeps the empty tracks at full width, so `justify-center`
      //     centres the TRACKS and the tiles still start hard left — three tiles
      //     and twelve tiles both began at the same x in the browser.
      // `auto-fit` collapses the unfilled tracks to zero, which is what lets a
      // partial row centre on the tiles that actually exist.
      <div
        className="grid grid-cols-[repeat(auto-fit,104px)] justify-center gap-x-2 gap-y-5"
        data-testid="launcher-grid"
      >
        {data.map(s => <Tile key={s.fileId} shortcut={s} onOpen={onOpen} />)}
      </div>
    )
  }

  return (
    // `launcher-root` is the SURFACE, `launcher-grid` below is its CONTENT, and
    // the distinction is what makes the no-flash e2e possible: the grid is
    // absent while loading and on an empty tenant, so "did the launcher ever
    // paint" cannot be asked of it. See e2e/landing-switch.spec.ts.
    <div className="flex-1 min-w-0 min-h-0 overflow-y-auto papan-thin-scroll" data-testid="launcher-root">
      {/* Narrower gutters below `sm`. At 360px, `px-6` leaves 312px of track,
          which `minmax(104px,1fr)` + an 8px gap fills with TWO columns; `px-4`
          leaves 328px, which is exactly three. Two columns of 64px icons on a
          phone reads as a list that lost its rows, not as a launcher. */}
      <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 sm:py-8">
        {/* The same header the chat home renders, so a deployment's landing
            surface greets its user identically whichever of the two it is set
            to. Org name is ABSOLUTE inside a fixed-padding block for the
            reason ChatHomeView gives: the greeting then sits at the same y
            with or without it, so switching to a personal tenant does not
            make the whole page jump.

            `pt-7` is therefore LOAD-BEARING, not taste — it is the height the
            absolute org name occupies, and the only thing holding it off the
            greeting. It matches ChatHomeView's exactly. A smaller value does
            not merely tighten the spacing: at `pt-1` the org name printed ON
            TOP of "Good evening", overlapping it by 17px. */}
        {/* `landing-greeting` / `landing-orgname` are the SHARED handles: the
            same two ids sit on ChatHomeView's copy of this header, so one spec
            can assert the header on whichever surface a deployment lands on
            without branching. A testid rather than the class or the copy, for
            the reason the taskbar rail carries one — `chat-home-greeting` is
            styling and the text is display copy, and both have moved. */}
        <div className="relative pt-7 pb-6 text-center">
          {orgName && (
            <p
              className="chat-home-orgname select-none absolute top-0 inset-x-0 truncate px-8"
              data-testid="landing-orgname"
            >
              {orgName}
            </p>
          )}
          <h1
            className="chat-home-greeting text-[30px] leading-tight themed-text"
            data-testid="landing-greeting"
          >
            {greeting}{firstName ? `, ${firstName}` : ''}
          </h1>
        </div>
        {/* `ws-home-heading` is a flex row, so centring is justify- not text-. */}
        <h2 className="ws-home-heading justify-center mb-5">{t('launcher.heading')}</h2>
        {body()}
      </div>
    </div>
  )
}
